import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  Button,
  Checkbox,
  EmptyState,
  Input,
  ReportTable,
  type ReportTablePagination,
  type ReportTableSort,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { ArrowLeft, Download } from 'lucide-react';
import {
  exportMarketingReportReportsMarketingExportGet,
  getMarketingReportOptionsReportsMarketingOptionsGet,
  getMarketingReportReportsMarketingGet,
} from '../services/api/sdk.gen';
import type {
  MarketingReportResult,
  MarketingReportRow,
  MarketingSortBy,
  SortOrder,
} from '../services/api/types.gen';
import { PermissionSlug } from '../constants/featureFlags';
import ReportsPermissionAlert from './ReportsPermissionAlert';
import { useReportsBackHref } from '../reporting/backHref';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import { useDebounce } from '../hooks/useDebounce';
import { downloadResponseAsFile } from '../utils/downloadFile';
import {
  getBusinessErrorMessage,
  getUserFacingApiMessageAsync,
} from '../utils/apiErrorDetail';
import {
  coerceReportPageSize,
  formatReportEmptyValue,
  readOptionalChoiceParam,
  readPositiveIntegerParam,
  REPORT_PAGE_SIZE_OPTIONS,
} from '../utils/reportDisplay';
import { formatDateToMMDDYYYY } from '../utils/dateUtils';

const ALL_VALUE = '__all__';
const VALID_SORTS: MarketingSortBy[] = [
  'referral_date',
  'patient_name',
  'mrn',
  // Keep legacy branch URLs valid while the visible column uses Clinic.
  'branch',
  'clinic',
  'internal_referral',
  'soc_date',
  'status',
];
const VALID_DIRECTIONS: SortOrder[] = ['asc', 'desc'];

/** Default window: the trailing 30 days, matching how the Kinnser screen is used. */
function defaultWindow(): { start: string; end: string } {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  return { start: toIsoDay(start), end: toIsoDay(end) };
}

function toIsoDay(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function dateCell(value: string | null): string {
  return value ? formatDateToMMDDYYYY(value) : formatReportEmptyValue(null);
}

function textCell(value: string | null) {
  return formatReportEmptyValue(value);
}

// Column order mirrors the source Marketing report while the Clinic label
// accurately describes the AgencyClinic value returned for each referral.
const columns = [
  {
    id: 'clinic',
    header: 'Clinic',
    accessor: (row: MarketingReportRow) => textCell(row.clinic),
    enableSorting: true,
    minWidth: 170,
  },
  {
    id: 'mrn',
    header: 'MR',
    accessor: (row: MarketingReportRow) => textCell(row.mrn),
    enableSorting: true,
    minWidth: 120,
  },
  {
    id: 'first_name',
    header: 'First Name',
    accessor: 'first_name' as const,
    render: (_value: unknown, row: MarketingReportRow) =>
      row.patient_id ? (
        <Link
          className='font-medium text-primary underline-offset-2 hover:underline'
          to={`/patients/${row.patient_id}`}
        >
          {row.first_name}
        </Link>
      ) : (
        textCell(row.first_name)
      ),
    minWidth: 130,
  },
  {
    id: 'middle_initial',
    header: 'MI',
    accessor: (row: MarketingReportRow) => textCell(row.middle_initial),
    minWidth: 60,
  },
  {
    // ReportTable round-trips `column.id` through the `sort` query param, so a
    // sortable column has to be named for the API sort key it maps to.
    id: 'patient_name',
    header: 'Last Name',
    accessor: (row: MarketingReportRow) => textCell(row.last_name),
    enableSorting: true,
    minWidth: 140,
  },
  {
    id: 'status',
    header: 'Status',
    accessor: 'status' as const,
    enableSorting: true,
    minWidth: 150,
  },
  {
    id: 'referral_date',
    header: 'Referral Date',
    accessor: (row: MarketingReportRow) => dateCell(row.referral_date),
    enableSorting: true,
    minWidth: 130,
  },
  {
    id: 'primary_insurance',
    header: 'Primary Insurance',
    accessor: (row: MarketingReportRow) => textCell(row.primary_insurance),
    minWidth: 170,
  },
  {
    id: 'physician_first',
    header: 'Physician First',
    accessor: (row: MarketingReportRow) => textCell(row.physician_first),
    minWidth: 140,
  },
  {
    id: 'physician_last',
    header: 'Physician Last',
    accessor: (row: MarketingReportRow) => textCell(row.physician_last),
    minWidth: 140,
  },
  {
    id: 'internal_referral_first',
    header: 'Internal Referral First',
    accessor: (row: MarketingReportRow) =>
      textCell(row.internal_referral_first),
    minWidth: 170,
  },
  {
    id: 'internal_referral',
    header: 'Internal Referral Last',
    accessor: (row: MarketingReportRow) => textCell(row.internal_referral_last),
    enableSorting: true,
    minWidth: 180,
  },
  {
    id: 'external_referral',
    header: 'External Referral',
    accessor: (row: MarketingReportRow) => textCell(row.external_referral),
    minWidth: 200,
  },
  {
    id: 'case_manager',
    header: 'Case Manager',
    accessor: (row: MarketingReportRow) => textCell(row.case_manager),
    minWidth: 160,
  },
  {
    id: 'soc_date',
    header: 'Start of Care Date',
    accessor: (row: MarketingReportRow) => dateCell(row.soc_date),
    enableSorting: true,
    minWidth: 150,
  },
  {
    id: 'episode_start',
    header: 'Episode Start',
    accessor: (row: MarketingReportRow) => dateCell(row.episode_start),
    minWidth: 130,
  },
  {
    id: 'episode_end',
    header: 'Episode End',
    accessor: (row: MarketingReportRow) => dateCell(row.episode_end),
    minWidth: 130,
  },
  {
    id: 'episode_length',
    header: 'Episode Length',
    accessor: (row: MarketingReportRow) =>
      row.episode_length === null
        ? formatReportEmptyValue(null)
        : String(row.episode_length),
    minWidth: 130,
  },
];

type Option = { id: string; label: string };
type OptionsResponse = {
  clinics: Option[];
  internal_referral_sources: Option[];
};

export default function MarketingReport() {
  const [searchParams, setSearchParams] = useSearchParams();
  const reportsBackHref = useReportsBackHref();
  const { hasPermission, showError } = useStore(
    useShallow(state => ({
      hasPermission: state.hasPermission,
      showError: state.showError,
    }))
  );

  const fallbackWindow = useMemo(defaultWindow, []);
  const startDate = searchParams.get('start') ?? fallbackWindow.start;
  const endDate = searchParams.get('end') ?? fallbackWindow.end;
  const clinic = searchParams.get('clinic') ?? '';
  const source = searchParams.get('source') ?? '';
  const unattributedOnly = searchParams.get('unattributed') === 'true';
  const pendingOnly = searchParams.get('pending') === 'true';
  const q = searchParams.get('q') ?? '';
  const [searchText, setSearchText] = useState(q);
  const page = readPositiveIntegerParam(searchParams.get('page'));
  const pageSize = coerceReportPageSize(Number(searchParams.get('size')));
  const sortBy = readOptionalChoiceParam(searchParams.get('sort'), VALID_SORTS);
  const sortOrder = readOptionalChoiceParam(
    searchParams.get('dir'),
    VALID_DIRECTIONS
  );

  const [report, setReport] = useState<MarketingReportResult | null>(null);
  const [options, setOptions] = useState<OptionsResponse>({
    clinics: [],
    internal_referral_sources: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const canAccess = hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS);

  const updateParams = useCallback(
    (updates: Record<string, string | number | boolean | null>) => {
      setSearchParams(current => {
        const params = new URLSearchParams(current);
        Object.entries(updates).forEach(([key, value]) => {
          if (value === null || value === '' || value === false) {
            params.delete(key);
          } else {
            params.set(key, String(value));
          }
        });
        return params;
      });
    },
    [setSearchParams]
  );

  const debouncedUpdateSearch = useDebounce((value: string) => {
    updateParams({ q: value || null, page: 1 });
  }, 350);

  useEffect(() => {
    setSearchText(q);
  }, [q]);

  const buildQuery = useCallback(
    () => ({
      start_date: startDate,
      end_date: endDate,
      clinic_id: clinic || null,
      internal_referral_source_ids: source || null,
      unattributed_only: unattributedOnly,
      pending_only: pendingOnly,
      q: q || null,
      sort_by: sortBy ?? null,
      sort_order: sortOrder ?? null,
    }),
    [
      startDate,
      endDate,
      clinic,
      source,
      unattributedOnly,
      pendingOnly,
      q,
      sortBy,
      sortOrder,
    ]
  );

  // Re-fetched when the Clinic changes: sources are assigned per clinic, so
  // another clinic's source could only ever be a filter that returns nothing.
  useEffect(() => {
    if (!canAccess) return;
    let cancelled = false;
    getMarketingReportOptionsReportsMarketingOptionsGet({
      query: { clinic_id: clinic || null },
    })
      .then(response => {
        if (!cancelled) setOptions(response.data as OptionsResponse);
      })
      .catch(error => {
        console.error('Failed to load marketing report options', error);
        if (!cancelled)
          showError(
            getBusinessErrorMessage(error) ??
              'Unable to load report filters. Please try again.'
          );
      });
    return () => {
      cancelled = true;
    };
  }, [canAccess, clinic, showError]);

  useEffect(() => {
    if (!canAccess) return;
    let cancelled = false;
    setIsLoading(true);
    setLoadError(false);
    setReport(null);
    getMarketingReportReportsMarketingGet({
      query: { ...buildQuery(), page, page_size: pageSize },
    })
      .then(response => {
        if (cancelled) return;
        if (response.error || !response.data) {
          setLoadError(true);
          showError('Unable to load marketing report. Please try again.');
          return;
        }
        setReport(response.data);
      })
      .catch(error => {
        console.error('Failed to load marketing report', error);
        if (!cancelled) {
          setLoadError(true);
          showError(
            getBusinessErrorMessage(error) ??
              'Unable to load marketing report. Please try again.'
          );
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canAccess, buildQuery, page, pageSize, showError]);

  const sorting = useMemo<ReportTableSort | null>(
    () =>
      sortBy
        ? {
            id: sortBy === 'branch' ? 'clinic' : sortBy,
            direction: sortOrder ?? 'asc',
          }
        : null,
    [sortBy, sortOrder]
  );
  const pagination = useMemo<ReportTablePagination>(
    () => ({
      page,
      pageSize,
      total: report?.total ?? 0,
      totalPages: Math.ceil((report?.total ?? 0) / pageSize),
      pageSizeOptions: REPORT_PAGE_SIZE_OPTIONS,
    }),
    [page, pageSize, report?.total]
  );

  const exportReport = async () => {
    setIsExporting(true);
    try {
      const response = await exportMarketingReportReportsMarketingExportGet({
        query: buildQuery(),
        responseType: 'blob' as const,
      });
      if (response.error || !response.data) {
        showError(
          await getUserFacingApiMessageAsync(
            {
              response: {
                status: response.response?.status,
                data: response.response?.data ?? response.error,
              },
            },
            'Unable to download marketing report. Please try again.'
          )
        );
        return;
      }
      downloadResponseAsFile(response as never, 'marketing-report.xlsx');
    } catch (error) {
      console.error('Failed to download marketing report', error);
      showError(
        await getUserFacingApiMessageAsync(
          error,
          'Unable to download marketing report. Please try again.'
        )
      );
    } finally {
      setIsExporting(false);
    }
  };

  if (!canAccess) return <ReportsPermissionAlert />;

  const emptyState = loadError ? (
    <EmptyState title='Unable to load report' description='Please try again.' />
  ) : (
    <EmptyState
      title='No referrals found'
      description='Adjust the date range or filters and try again.'
    />
  );

  return (
    <div className='space-y-6'>
      <Link
        to={reportsBackHref}
        className='inline-flex items-center text-sm font-medium text-muted-foreground hover:text-foreground'
      >
        <ArrowLeft className='mr-1 h-4 w-4' /> Reports
      </Link>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div>
          <h1 className='text-2xl font-bold'>Marketing Report</h1>
          <p className='text-sm text-muted-foreground'>
            Referrals received in a date range and who brought them in.
          </p>
        </div>
        <Button
          variant='outline'
          size='sm'
          onClick={() => void exportReport()}
          disabled={isExporting || isLoading}
        >
          <Download className='h-4 w-4' /> Export
        </Button>
      </div>

      <Alert>
        <AlertDescription>
          Rows are referrals grouped by the date they were received, not the
          start of care date. Referrals not yet pushed to the EMR appear as
          Pending Admissions.
        </AlertDescription>
      </Alert>

      <div className='grid gap-3 md:grid-cols-5'>
        <label className='space-y-1 text-sm font-medium'>
          Referral date from
          <Input
            type='date'
            inputSize='default'
            value={startDate}
            onChange={event =>
              updateParams({ start: event.target.value, page: 1 })
            }
          />
        </label>
        <label className='space-y-1 text-sm font-medium'>
          Referral date to
          <Input
            type='date'
            inputSize='default'
            value={endDate}
            onChange={event =>
              updateParams({ end: event.target.value, page: 1 })
            }
          />
        </label>
        <label className='space-y-1 text-sm font-medium'>
          Clinic
          <Select
            value={clinic || ALL_VALUE}
            onValueChange={next =>
              updateParams({
                clinic: next === ALL_VALUE ? '' : next,
                // Sources belong to a clinic, so changing clinics clears the
                // old source filter before it can return an empty report.
                source: null,
                page: 1,
              })
            }
          >
            <SelectTrigger selectSize='default'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_VALUE}>All clinics</SelectItem>
              {options.clinics.map(option => (
                <SelectItem key={option.id} value={option.id}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <label className='space-y-1 text-sm font-medium'>
          Internal referral source
          <Select
            value={source || ALL_VALUE}
            disabled={unattributedOnly}
            onValueChange={next =>
              updateParams({
                source: next === ALL_VALUE ? '' : next,
                page: 1,
              })
            }
          >
            <SelectTrigger selectSize='default'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_VALUE}>All sources</SelectItem>
              {options.internal_referral_sources.map(option => (
                <SelectItem key={option.id} value={option.id}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <label className='space-y-1 text-sm font-medium md:col-span-1'>
          Search
          <Input
            type='search'
            inputSize='default'
            value={searchText}
            onChange={event => {
              const value = event.target.value;
              setSearchText(value);
              debouncedUpdateSearch(value);
            }}
            placeholder='Patient, MRN, or source'
          />
        </label>
        <label className='flex items-center gap-2 text-sm font-medium'>
          <Checkbox
            checked={unattributedOnly}
            onCheckedChange={checked =>
              updateParams({ unattributed: checked === true, page: 1 })
            }
          />
          Only referrals with no internal source
        </label>
        <label className='flex items-center gap-2 text-sm font-medium'>
          <Checkbox
            checked={pendingOnly}
            onCheckedChange={checked =>
              updateParams({ pending: checked === true, page: 1 })
            }
          />
          Only referrals not yet pushed
        </label>
      </div>

      <ReportTable
        data={report?.items ?? []}
        columns={columns}
        getRowId={row => row.intake_id}
        caption='Marketing report results'
        sorting={sorting}
        onSortingChange={next =>
          updateParams(
            next
              ? {
                  sort:
                    next.id === 'clinic' && sortBy === 'branch'
                      ? 'branch'
                      : next.id,
                  dir: next.direction,
                  page: 1,
                }
              : { sort: null, dir: null, page: 1 }
          )
        }
        isLoading={isLoading}
        emptyState={emptyState}
        className='min-w-[2200px] text-xs'
        pagination={pagination}
        onPaginationChange={next =>
          updateParams({ page: next.page, size: next.pageSize })
        }
      />
    </div>
  );
}
